import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useCalendarUI } from '../context/CalendarContext';
import { useCalendarSettings } from './useCalendarSettings';
import { generateGoogleAuthURL, generateGoogleToken, disconnectGoogleCalendar } from '../api/calendarApi';
import { useToast } from 'context/ToastContext';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { getQueryParams } from 'utils/utils';
import { toastConstant } from 'constants/toastmessage';
import { useGoogleCalendarList } from './useGoogleCalendarList';
import { queryKeys } from 'hooks/config/queryClient';
import { queryClient } from 'config/queryClient';

/**
 * Default form values matching API format
 * API uses these exact keys - do not modify
 */
const DEFAULT_FORM_VALUES = {
  show_weekends: true,
  show_birthdays: true,
  show_sol: true,
  show_business_times_only: false,
  sync_with_google_calendar: false,
  sync_with_outlook: false,
  calendar_id: null,
  default_calendar_view: 'month',
  primary: true,
  // business_start_time: '09:00',
  // business_end_time: '17:00',
};

export const useCalendarSettingsLogic = () => {
  const { modals, closeSettingsModal } = useCalendarUI();
  const { settings, updateSettings, isUpdating } = useCalendarSettings();
  const { addToast } = useToast();
  const { userDetails } = useUserDetailsContext();

  // Connection loading states
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false);
  const [isDisconnectingGoogle, setIsDisconnectingGoogle] = useState(false);
  const [isConnectingOutlook, setIsConnectingOutlook] = useState(false);

  // React Hook Form setup
  const form = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange', // Validate on change for better UX
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty, isValid },
  } = form;

  // Watch form values for conditional logic (using API format keys)
  const showBusinessTimeOnly = watch('show_business_times_only');
  const syncWithGoogleCalendar = watch('sync_with_google_calendar');

  // Fetch Google calendar list when connected
  const {
    googleCalendars,
    isLoading: isLoadingCalendars,
    refetch: refetchCalendars,
  } = useGoogleCalendarList(syncWithGoogleCalendar && modals?.settings);

  // Initialize form with fetched settings (API format)
  useEffect(() => {
    if (settings) {
      reset({
        ...DEFAULT_FORM_VALUES,
        ...settings,
      });
    }
  }, [settings, reset]);

  // Reset form to original settings when modal opens
  useEffect(() => {
    if (modals?.settings && settings) {
      reset({
        ...DEFAULT_FORM_VALUES,
        ...settings,
      });
    }
  }, [modals?.settings, settings, reset]);

  // Google Calendar connection handler
  const handleGoogleConnect = async () => {
    if (!userDetails?.firm_user_id) {
      addToast(false, 'error', 'Error', 'User details not available');
      return;
    }

    setIsConnectingGoogle(true);
    try {
      const response = await generateGoogleAuthURL(userDetails.firm_user_id);
      const authUrl = response.data?.auth_url;

      if (authUrl) {
        // Open popup for OAuth flow
        const popup = window.open(authUrl, 'google-oauth', 'width=600,height=650,scrollbars=yes,resizable=yes');

        // Poll for popup closure and verify connection
        const pollTimer = setInterval(async () => {
          try {
            if (popup.document.URL.indexOf('code') != -1) {
              popup.close();
            }
          } catch (error) {}
          if (popup.closed) {
            clearInterval(pollTimer);

            try {
              const params = getQueryParams(popup.document.URL);

              generateGoogleToken(userDetails.firm_user_id, params?.code, params?.state)
                .then(async () => {
                  setValue('sync_with_google_calendar', true);
                  await updateSettings({
                    ...settings,
                    sync_with_google_calendar: true,
                  });
                  // Fetch calendar list after successful connection
                  setTimeout(() => {
                    queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
                  }, 1000);
                  setIsConnectingGoogle(false);
                })
                .catch((error) => {
                  setIsConnectingGoogle(false);
                  console.error('Failed to generate Google token:', error);
                  addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Error occurred. Please try again.');
                });
            } catch (verificationError) {
              setIsConnectingGoogle(false);
              console.error('Google Calendar verification failed:', verificationError);
              addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Error occurred. Please try gain.');
            }
          }
        }, 1000);
      } else {
        throw new Error('No auth URL received');
      }
    } catch (error) {
      console.error('Google Calendar connection failed:', error);
      addToast(false, toastConstant.toasterType.ERROR, 'Error', 'Failed to connect Google Calendar');
      setIsConnectingGoogle(false);
    }
  };

  // Google Calendar disconnect handler
  const handleGoogleDisconnect = async () => {
    if (!userDetails?.firm_user_id) {
      addToast(false, toastConstant.toasterType.ERROR, 'Error', 'User details not available');
      return;
    }

    setIsDisconnectingGoogle(true);
    try {
      await disconnectGoogleCalendar(userDetails.firm_user_id);

      setValue('sync_with_google_calendar', false);
      setValue('calendar_id', null);
      await updateSettings({
        ...settings,
        sync_with_google_calendar: false,
        calendar_id: null,
      });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
      }, 1000);

      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Google Calendar disconnected successfully');
    } catch (error) {
      console.error('Google Calendar disconnect failed:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to disconnect Google Calendar');
    } finally {
      setIsDisconnectingGoogle(false);
    }
  };

  // Outlook connection handler (simulated)
  const handleOutlookConnect = async () => {
    setIsConnectingOutlook(true);

    try {
      // Simulate Outlook OAuth flow (would need real implementation)
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Mock successful connection with calendar ID
      const mockOutlookCalendarId = 'user@outlook.com';

      // Update form with sync status and calendar ID
      setValue('sync_with_outlook', true);
      setValue('calendar_id', mockOutlookCalendarId);
    } catch (error) {
      console.error('Outlook Calendar connection failed:', error);
      setIsConnectingOutlook(false);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to connect Outlook Calendar');
    }
  };

  // Outlook disconnect handler
  const handleOutlookDisconnect = () => {
    setValue('sync_with_outlook', false);
    setValue('calendar_id', null);
    addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Outlook Calendar disconnected');
  };

  // Form submission handler
  const onSubmit = (formData) => {
    // Update settings via mutation (API + React Query cache)
    // Context will be automatically updated via useEffect in CalendarStateProvider
    updateSettings(formData, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
        closeSettingsModal();
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Calendar settings saved successfully');
      },
      onError: (error) => {
        console.error('Failed to save settings:', error);
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to save calendar settings');
      },
    });
  };

  // Form submission with validation
  const handleSave = handleSubmit(onSubmit);

  return {
    // Form state
    form,
    errors,
    isDirty,
    isValid,

    // Form values for conditional rendering (using API format)
    showBusinessTimeOnly,
    googleEmail: syncWithGoogleCalendar,

    // Google Calendar data
    googleCalendars,
    isLoadingCalendars,

    // Loading states
    isUpdating,
    isConnectingGoogle,
    isDisconnectingGoogle,
    isConnectingOutlook,

    // Modal state
    isOpen: modals.settings,

    // Handlers
    handleSave,
    handleGoogleConnect,
    handleGoogleDisconnect,
    handleOutlookConnect,
    handleOutlookDisconnect,
    closeSettingsModal,
  };
};

export default useCalendarSettingsLogic;
